import { useCallback, useState, memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { useMatch, useNavigate, useSearch } from '@tanstack/react-location';
import { ContactInfo } from '@weave/schema-gen-ts/dist/schemas/persons/v3/contact_info.pb';
import { PersonsResponse } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons_service.pb';
import { IntegrationType } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import {
  ContactType_Enum,
  PersonOrderBy_Enum,
  PersonStatus_Enum,
} from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { InfiniteData, useQueryClient } from 'react-query';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonAPI, PersonTypes } from '@frontend/api-person';
import { SchemaPetsV1Service } from '@frontend/api-pets';
import { FilterGroupHeader, FilterGroupSection, InfinitePaginatedList } from '@frontend/components';
import { ContactChip } from '@frontend/contact-panel';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useContainerQuery } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore, useScopedInfiniteQuery, useScopedQuery } from '@frontend/scope';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import {
  ContentPanel,
  Heading,
  Layouts,
  useFormField,
  useAlert,
  IconButton,
  PopoverDialog,
  usePopoverDialog,
  RadioField,
  SearchField,
  useDebouncedValue,
  Text,
  SwitchChipGroup,
  SkeletonLoader,
  Chip,
  Button,
  type BaseFieldProps,
} from '@frontend/design-system';
import { ContactListRow } from '../components';
import { useAllIntegrationDetailsQuery } from '../components/hooks';
import { VetContactListRow } from '../components/vet-contact-list-row';
import { getContactFilterStatusLabels } from '../store/contacts-store';
import { ContactStatus } from '../types';

type FindContactInfoParamsType = {
  contactInfo?: ContactInfo[];
  contactType: ContactType_Enum;
};

type PersonV3 = PersonsResponse['persons'][number];

type PanelHeaderProps = {
  search: Partial<{ selectedLabel: string }>;
  searchContactProps: BaseFieldProps<string, '', HTMLInputElement>;
  children?: React.ReactNode;
};

type ContactsSearchProps = {
  searchContactProps: BaseFieldProps<string, '', HTMLInputElement>;
  isSmallModeSearchOpen: boolean;
  isContainerSmall: boolean;
  handleSmallModeChange: (value: boolean) => void;
};

type ContactsListProps = {
  filter: string;
  sortContactBy: string;
  search: string;
};

const API_RESPONSE_LIMIT = 50;

const getContactSearchKey = (filter: string, sortContactBy: string, search: string, sourceIds: string[]) => [
  'contacts-list',
  filter,
  sortContactBy,
  search,
  sourceIds,
];

const toLegacySourceType = (sourceType?: IntegrationType) => {
  if (!sourceType) {
    return undefined;
  }
  if (sourceType === IntegrationType.FILE_UPLOAD) {
    return PersonTypes.PersonSourceType.Csv;
  } else if (sourceType === IntegrationType.MANUAL_ENTRY) {
    return PersonTypes.PersonSourceType.CustomContact;
  } else if (sourceType === IntegrationType.INTEGRATION_TYPE_UNSPECIFIED) {
    return undefined;
  } else {
    return PersonTypes.PersonSourceType.Integration;
  }
};

const convertToLegacyPerson = (
  person: PersonV3,
  getIntegrationDetails: ReturnType<typeof useAllIntegrationDetailsQuery>['getIntegrationDetails']
) => {
  const findContactInfoByType = ({ contactInfo, contactType }: FindContactInfoParamsType) =>
    contactInfo?.find((contactInfoItem) => contactInfoItem.type === contactType)?.destination || '';

  return {
    PreferredName: person.preferredName ?? '',
    FirstName: person.firstName ?? '',
    LastName: person.lastName ?? '',
    PersonID: person.personId,
    ContactInfo: person.contactInfo,
    Notes: person.notes ?? '',
    SourceID: person.sourceId ?? '',
    MobilePhone: findContactInfoByType({
      contactInfo: person?.contactInfo,
      contactType: ContactType_Enum.PHONE_MOBILE,
    }),
    Email: findContactInfoByType({ contactInfo: person?.contactInfo, contactType: ContactType_Enum.EMAIL }),
    HomePhone: findContactInfoByType({
      contactInfo: person?.contactInfo,
      contactType: ContactType_Enum.PHONE_HOME,
    }),
    PMID: person.personPmid ?? '',
    PMDisplayId: person.personDisplayPmid ?? '',
    Gender: person.gender ?? '',
    Status: titleCase(PersonStatus_Enum[person.status || PersonStatus_Enum.UNSPECIFIED]),
    SourceType: toLegacySourceType(getIntegrationDetails(person.sourceId)?.integration?.type),
    Birthdate: person.birthdate ?? '',
  } as unknown as PersonTypes.Person;
};

const PanelHeaderTitle = ({ filter }: { filter: string }) => {
  const tokens = useTranslationTokens();

  const getTitle = (filter: string) => {
    switch (filter) {
      case 'all-contacts':
        return tokens.allContacts;
      case 'active':
        return tokens.activeContacts;
      case 'inactive':
        return tokens.inactiveContacts;
      default:
        return tokens.allContacts;
    }
  };

  return <Heading>{getTitle(filter)}</Heading>;
};

const CreateContactButton = () => {
  const alert = useAlert();
  const { t } = useTranslation('contacts');
  const queryClient = useQueryClient();
  const { setShow } = useCustomContactFormSlidePanel();
  const { selectedLocationIds } = useAppScopeStore();
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const tokens = useTranslationTokens();

  const isCustomContactsEnabled = getCustomizationFlagValue('customcontacts');

  const createContact = useCallback(() => {
    setShow({
      show: true,
      context: {
        mode: 'create',
        onSave: async (data) => {
          alert.success(t('Contact created successfully'));
          queryClient.invalidateQueries({ queryKey: [selectedLocationIds, 'contacts'] });

          /**
           * Put the person at the top of the contact list, temporarily
           */

          const personId = data.data.person_id;
          const person = await PersonAPI.getPersonExtended(personId);

          queryClient.setQueriesData<
            | InfiniteData<{
                rows: PersonTypes.Person[];
                nextOffset: string;
              }>
            | undefined
          >(
            {
              active: true,
              predicate: ({ queryKey }) => queryKey.includes('contacts-list'),
            },
            (prev) => {
              if (!prev) {
                return prev;
              }
              const { pages, ...rest } = prev;
              return {
                ...rest,
                pages: pages.map((page) => {
                  return {
                    ...page,
                    rows: [person, ...page.rows],
                  };
                }),
              };
            }
          );
        },
        onError: () => {
          alert.error(t('Contact creation error'));
        },
      },
    });
  }, [selectedLocationIds, alert, t, queryClient, setShow]);

  if (!isCustomContactsEnabled) return null;

  return (
    <Button
      trackingId='contact-2.0-list-button-create'
      css={{ width: 'auto' }}
      size='large'
      iconName='plus'
      onClick={createContact}
    >
      {tokens.createContact}
    </Button>
  );
};

const StatusFilterChips = memo(
  ({
    search,
    isSmallModeSearchOpen,
    isLoading,
    contactStatuses,
  }: {
    search: Partial<{ selectedLabel: string }>;
    isSmallModeSearchOpen: boolean;
    isLoading: boolean;
    contactStatuses: ReturnType<typeof getContactFilterStatusLabels>;
  }) => {
    const { t } = useTranslation('contacts');
    const navigate = useNavigate();

    const userSelectedLabel = search.selectedLabel || ContactStatus.ACTIVE;
    const isAllSelected = search.selectedLabel === 'All';

    const contactStatusesEntries = useMemo(() => Object.entries(contactStatuses), [contactStatuses]);

    const replaceUrl = (filter: string) => {
      navigate({ search: { ...search, selectedLabel: filter }, replace: true });
    };

    if (isLoading) {
      return <SkeletonLoader animation='shimmer' width={200} height={40} count={1} distance={10} />;
    }

    if (isSmallModeSearchOpen) {
      return null;
    }

    return (
      <>
        <Chip
          css={{ cursor: 'pointer' }}
          className={`status-filter-chips-group ${isAllSelected ? 'selected' : ''}`}
          onClick={() => replaceUrl('All')}
          size='large'
          variant='outline'
          trackingId='patients-status-filter-button-all'
        >
          <Text size='medium'>{t('All')}</Text>
        </Chip>
        <div
          css={{
            display: 'block',
            margin: theme.spacing(1),
            height: theme.spacing(3),
            borderLeft: `1px solid ${theme.colors.neutral20}`,
          }}
        />
        <SwitchChipGroup
          css={{ gap: theme.spacing(1), marginRight: theme.spacing(1) }}
          singleSelect
          value={[userSelectedLabel]}
          setValue={(val) => replaceUrl(val.toString())}
          checkboxMode
          defaultValue={ContactStatus.ACTIVE}
        >
          {contactStatusesEntries.map(([status, value]) => {
            if (value.available)
              return (
                <SwitchChipGroup.Option
                  key={status}
                  value={status}
                  trackingId={`patients-status-filter-button-${status.toLowerCase()}`}
                  className={`status-filter-chips-group ${userSelectedLabel?.includes(status) ? 'selected' : ''}`}
                >
                  <Text size='medium'>{value.label}</Text>
                </SwitchChipGroup.Option>
              );

            return null;
          })}
        </SwitchChipGroup>
      </>
    );
  }
);

const PanelHeader = ({ search, children, searchContactProps }: PanelHeaderProps) => {
  const match = useMatch();
  const { selectedLocationIds } = useAppScopeStore();
  const [isSmallModeSearchOpen, setIsSmallModeSearchOpen] = useState(false);

  const handleSmallModeChange = useCallback(
    (value: boolean) => {
      setIsSmallModeSearchOpen(value);
    },
    [setIsSmallModeSearchOpen]
  );

  const handleIsMatchChange = useCallback(
    (isMatch: boolean) => {
      if (!isMatch) {
        setIsSmallModeSearchOpen(false);
      }
    },
    [setIsSmallModeSearchOpen]
  );

  const [isContainerSmall, containerRef] = useContainerQuery({
    maxWidth: 600,
    onMatchChange: handleIsMatchChange,
  });

  const { data, isLoading } = useScopedQuery({
    queryKey: [PersonStatus_Enum.PROSPECTIVE, PersonOrderBy_Enum.LAST_NAME],
    queryFn: async () => {
      const personListData = await PersonAPI.getMultiPersonsV3({
        responseLimit: API_RESPONSE_LIMIT,
        search: '',
        locationIds: selectedLocationIds,
        statusFilter: PersonStatus_Enum.PROSPECTIVE,
        orderBy: PersonOrderBy_Enum.LAST_NAME,
      });

      return personListData.rows.length > 0 ? personListData.rows : null;
    },
  });

  const contactStatuses = useMemo(() => getContactFilterStatusLabels(!!data?.length), [!!data?.length]);

  return (
    <div
      ref={containerRef}
      css={{
        '.status-filter-chips-group': {
          ':focus': { outline: 'none' },
        },
        '.status-filter-chips-group.selected': {
          border: `1px solid ${theme.colors.primary50}`,
          background: theme.colors.primary5,
        },
      }}
    >
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <PanelHeaderTitle filter={match.params.filter} />
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <CreateContactButton />
        </div>
      </div>
      <Text css={{ color: theme.colors.neutral50, marginTop: theme.spacing(1) }}>View and manage your patients.</Text>
      <div css={{ display: 'flex', marginTop: theme.spacing(3) }}>
        <StatusFilterChips
          search={search}
          isSmallModeSearchOpen={isSmallModeSearchOpen}
          isLoading={isLoading}
          contactStatuses={contactStatuses}
        />
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${theme.spacing(1)};
            flex: 1;
            justify-content: flex-end;
          `}
        >
          <ContactsSearch
            isContainerSmall={isContainerSmall}
            searchContactProps={searchContactProps}
            isSmallModeSearchOpen={isSmallModeSearchOpen}
            handleSmallModeChange={handleSmallModeChange}
          />
          {children}
        </div>
      </div>
    </div>
  );
};

const searchFieldStyles = css`
  width: 100%;
  max-width: 400px;
`;

const ContactsSearch = memo(
  ({ searchContactProps, isSmallModeSearchOpen, isContainerSmall, handleSmallModeChange }: ContactsSearchProps) => {
    const { t } = useTranslation('contacts');

    const handleSearchModeEnter = useCallback(() => {
      handleSmallModeChange(true);
    }, [handleSmallModeChange]);

    const handleSearchModeExit = useCallback(() => {
      handleSmallModeChange(false);
    }, [handleSmallModeChange]);

    if (!isContainerSmall)
      return (
        <SearchField
          {...searchContactProps}
          containerCss={searchFieldStyles}
          clearable
          data-trackingid='contacts-feature-search-bar'
          name='search-contact'
        />
      );

    return (
      <>
        {isSmallModeSearchOpen ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(1)};
              justify-content: flex-end;
              width: 100%;
            `}
          >
            <SearchField
              {...searchContactProps}
              containerCss={searchFieldStyles}
              css={searchFieldStyles}
              clearable
              data-trackingid='contacts-feature-search-bar'
              name='search-contact'
            />
            <Button onClick={handleSearchModeExit} size='large' variant='secondary'>
              {t('Done')}
            </Button>
          </div>
        ) : (
          <Button onClick={handleSearchModeEnter} size='large' iconName='search' variant='secondary' />
        )}
      </>
    );
  }
);

export function Contacts() {
  const searchContactProps = useFormField({
    type: 'text',
  });
  const debouncedValue = useDebouncedValue(searchContactProps.value);

  const sortContactProps = useFormField({
    type: 'radio',
    value: 'last-name',
  });
  const { t } = useTranslation('contacts');
  const search = useSearch<{ Search: { selectedLabel: string } }>();

  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>();

  return (
    <>
      <Layouts.MultiPanel>
        <ContentPanel>
          <ContentPanel.Header>
            <PanelHeader search={search} searchContactProps={searchContactProps}>
              <IconButton showLabelOnHover {...getTriggerProps()} label='Sort' css={{ marginRight: theme.spacing(1) }}>
                <Icon name='sort-descending' />
              </IconButton>
            </PanelHeader>
          </ContentPanel.Header>
          <ContentPanel.Body style={{ display: 'block' }}>
            <ContactsPaginatedListSwitcher
              sortContactBy={sortContactProps.value}
              filter={search.selectedLabel || PersonStatus_Enum.ACTIVE}
              search={debouncedValue}
            />
          </ContentPanel.Body>
        </ContentPanel>
      </Layouts.MultiPanel>
      <PopoverDialog {...getDialogProps()} css={{ padding: 0, borderRadius: theme.borderRadius.medium, minWidth: 250 }}>
        <FilterGroupSection>
          <FilterGroupHeader headerText={t('Sort by')} />
        </FilterGroupSection>
        <FilterGroupSection>
          <RadioField {...sortContactProps} name='sort-contacts'>
            <RadioField.Radio value={'first-name'}>{t(`First Name`)}</RadioField.Radio>
            <RadioField.Radio value={'last-name'}>{t(`Last Name`)}</RadioField.Radio>
          </RadioField>
        </FilterGroupSection>
      </PopoverDialog>
    </>
  );
}

const ContactsPaginatedListSwitcher = ({ filter, sortContactBy, search }: ContactsListProps) => {
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');

  return lastUsedVertical === 'VET' ? (
    <VetContactsList filter={filter} sortContactBy={sortContactBy} search={search} />
  ) : (
    <NonVetContacts filter={filter} sortContactBy={sortContactBy} search={search} />
  );
};

const NonVetContacts = ({ filter, sortContactBy, search }: ContactsListProps) => {
  const { selectedLocationIds, getScopeName } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showNameAsFirstandLastName = getFeatureFlagValue('show-first-and-last-name');
  const { getLocationsByDatasource } = DataSourcesHooks.useDatasourceToLocations(selectedLocationIds, getScopeName);
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const { getIntegrationDetails, getAllDataSourceIds, isLoading, isSuccess } = useAllIntegrationDetailsQuery();
  const enumKey = filter.toUpperCase() as keyof typeof PersonStatus_Enum;
  const infiniteQueryProps = useScopedInfiniteQuery({
    enabled: !isLoading && isSuccess,
    queryKey: getContactSearchKey(filter, sortContactBy, search, demoSourceIds),
    queryFn: async (queryFunctionContext) =>
      PersonAPI.getMultiPersonsV3({
        responseLimit: API_RESPONSE_LIMIT,
        pageToken: queryFunctionContext.pageParam,
        orderBy: sortContactBy === 'last-name' ? PersonOrderBy_Enum.LAST_NAME : PersonOrderBy_Enum.FIRST_NAME,
        search,
        statusFilter: filter === 'All' ? '' : PersonStatus_Enum[enumKey],
        locationIds: selectedLocationIds,
        sourceIds: demoSourceIds,
      }),
    getNextPageParam: (prevGroup) => {
      if (prevGroup.rows.length < API_RESPONSE_LIMIT) return undefined;
      return prevGroup.nextOffset;
    },
    select: (data) => {
      const allValidDataSources = getAllDataSourceIds();
      return {
        pages: data.pages.map((page) => {
          return {
            ...page,
            rows: page.rows
              .filter((person) => allValidDataSources?.includes(person.sourceId || ''))
              .map((person) => convertToLegacyPerson(person, getIntegrationDetails)),
          };
        }),
        pageParams: data.pageParams,
      };
    },
  });

  return (
    <InfinitePaginatedList
      infiniteQueryProps={infiniteQueryProps}
      renderListItem={({ listItem }) => (
        <ContactListRow
          listItem={listItem}
          getLocationsByDatasource={getLocationsByDatasource}
          showNameAsFirstandLastName={showNameAsFirstandLastName}
        >
          {listItem.SourceType !== undefined ? <ContactChip.Source status={listItem.SourceType} /> : null}
          {listItem.Status && <ContactChip.Status status={listItem.Status} />}
        </ContactListRow>
      )}
    />
  );
};

const VetContactsList = ({ sortContactBy, filter, search }: ContactsListProps) => {
  const { selectedLocationIds, getScopeName } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showNameAsFirstandLastName = getFeatureFlagValue('show-first-and-last-name');
  const { getLocationsByDatasource } = DataSourcesHooks.useDatasourceToLocations(selectedLocationIds, getScopeName);
  const { getIntegrationDetails, isLoading, isSuccess } = useAllIntegrationDetailsQuery();
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const enumKey = filter.toUpperCase() as keyof typeof PersonStatus_Enum;

  const infiniteQueryProps = useScopedInfiniteQuery({
    queryKey: getContactSearchKey(filter, sortContactBy, search, demoSourceIds),
    enabled: !isLoading && isSuccess,
    queryFn: async (queryFunctionContext) => {
      const data = await PersonAPI.getMultiPersonsV3({
        responseLimit: API_RESPONSE_LIMIT,
        pageToken: queryFunctionContext.pageParam,
        search,
        orderBy: sortContactBy === 'last-name' ? PersonOrderBy_Enum.LAST_NAME : PersonOrderBy_Enum.FIRST_NAME,
        statusFilter: filter === 'All' ? '' : PersonStatus_Enum[enumKey],
        locationIds: selectedLocationIds,
        sourceIds: demoSourceIds,
      });
      // pets are not returned in the initial response, so we need to fetch them for each person
      const convertedPersonsMappedToPets = await Promise.all(
        data.rows.map(async (person) => {
          const { pets: petList = [] } = await SchemaPetsV1Service.FetchPetsByPersonId({
            personId: person.personId,
            locationId: selectedLocationIds[0],
          });

          return { ...convertToLegacyPerson(person, getIntegrationDetails), pets: petList };
        })
      );
      return { nextOffset: data.nextOffset, rows: convertedPersonsMappedToPets };
    },
    getNextPageParam: (prevGroup) => {
      if (prevGroup.rows.length < API_RESPONSE_LIMIT) return undefined;
      return prevGroup.nextOffset;
    },
  });

  return (
    <InfinitePaginatedList
      infiniteQueryProps={infiniteQueryProps}
      renderListItem={({ listItem }) => (
        <VetContactListRow
          listItem={listItem}
          getLocationsByDatasource={getLocationsByDatasource}
          isLoading={infiniteQueryProps.isLoading}
          showNameAsFirstandLastName={showNameAsFirstandLastName}
        >
          {listItem.SourceType !== undefined ? <ContactChip.Source status={listItem.SourceType} /> : null}
          {listItem.Status && <ContactChip.Status status={listItem.Status} />}
        </VetContactListRow>
      )}
    />
  );
};
